import { get } from 'lodash';

import { Controller } from 'react-hook-form';

import { FormItem, NumberInput } from '@ingrid/luterkort-react';

import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';
import { ValidationError } from '~src/components/ValidationError/ValidationError';

import { ValidateSchema } from '../../create/validate';
import { DynamicPath, useCarrierContractFormContext } from '../../types';
import { Schema } from '../queries';
import { SchemaFormLabel } from './schema-form-label';

interface Props {
  schemaId: string;
  schema: Schema;
  path?: string;
}

function SchemaFormIntegerOneOf({ schema, schemaId, path = '' }: Props) {
  const { formState, control } = useCarrierContractFormContext();

  if (schema.type !== 'integer') {
    return null;
  }

  return (
    <Controller
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      control={control}
      render={({ field }) => (
        <SimpleSelect
          options={(schema.oneOf ?? []).map(({ const: value, title }) => [value.toString(), title])}
          onValueChange={([value]) => field.onChange(+value)}
          invalid={!!get(formState.errors, path)?.message}
          value={[String(field.value as unknown as string)]}
          ref={field.ref}
        />
      )}
    />
  );
}

function SchemaFormIntegerNumber({ schemaId, path = '' }: Props) {
  const { formState, control } = useCarrierContractFormContext();

  return (
    <Controller
      control={control}
      rules={ValidateSchema(path, schemaId)}
      name={path as DynamicPath}
      render={({ field }) => (
        <FormItem.Control invalid={!!get(formState.errors, path)?.message}>
          <NumberInput {...field} onChange={event => field.onChange(event)} value={field.value as number | undefined} />
        </FormItem.Control>
      )}
    />
  );
}

export function SchemaFormInteger({ schema, schemaId, path = '' }: Props) {
  const { formState } = useCarrierContractFormContext();

  if (schema.type !== 'integer') {
    return null;
  }

  return (
    <FormItem.Root>
      <SchemaFormLabel schema={schema} path={path} schemaId={schemaId} />

      {schema.oneOf ? (
        <SchemaFormIntegerOneOf schema={schema} schemaId={schemaId} path={path} />
      ) : (
        <SchemaFormIntegerNumber schema={schema} schemaId={schemaId} path={path} />
      )}

      <ValidationError errors={formState.errors} name={path} />
    </FormItem.Root>
  );
}
